import { Avatar, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { availableIconFormat, documnentIcons } from 'helpers/documentIcons';
import { TFunction } from 'i18next';
import { IRecycleBinItem, IUserData } from 'interfaces';
import classes from '../RecycleBinTable.module.scss';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import cn from 'classnames';
import { QuestionCircleOutlined } from '@ant-design/icons';
import Assets from 'assets/icons';

const RETENTION_DAYS = 30;

const calculateDaysLeft = (deletedAt: string): number => {
  const deletedDate = dayjs(deletedAt);
  const expirationDate = deletedDate.add(RETENTION_DAYS, 'day');
  return Math.max(0, expirationDate.diff(dayjs(), 'day'));
};


const getTypeFilters = (data: IRecycleBinItem[], t: (key: string) => string) => {
  const types = new Set<string>();

  data.forEach((item) => {
    if (item.type === 'folder') {
      types.add('folder');
    } else if (item.extension) {
      types.add(item.extension.toLowerCase());
    }
  });

  const filters: { text: string; value: string }[] = [];

  if (types.has('folder')) {
    filters.push({ text: t('RecycleBin.filters.folder'), value: 'folder' });
  }

  types.forEach((type) => {
    if (type !== 'folder') {
      filters.push({ text: type.toUpperCase(), value: type });
    }
  });

  return filters;
};

const getUserFilters = (data: IRecycleBinItem[]) => {
  const usersMap = new Map<string, string>();

  data.forEach((item) => {
    if (item.deletion_author && !usersMap.has(item.deletion_author.id)) {
      usersMap.set(item.deletion_author.id, item.deletion_author.name);
    }
  });

  return Array.from(usersMap.entries()).map(([id, name]) => ({
    text: name,
    value: id,
  }));
};

export const columnsRecycle = (t: TFunction, data: IRecycleBinItem[], userData: IUserData | null): ColumnsType<IRecycleBinItem> => [
    {
      title: t('RecycleBin.table.name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      showSorterTooltip: { mouseEnterDelay: 1 },
      filters: getTypeFilters(data, t),
      onFilter: (value, record) => {
        if (value === 'folder') return record.type === 'folder';
        return record.extension?.toLowerCase() === value;
      },
      ellipsis: true,
      render: (_: string, record: IRecycleBinItem) => {
        const fileType = record.type === 'folder'
          ? 'folder'
          : (record.extension?.toLowerCase() || 'default');
        const icon = availableIconFormat.includes(fileType) ? fileType : 'default';        

        return (
          <div className={classes.nameCell}>
            <div className={classes.iconWrapper}>
              <Avatar
                className={classes.avatar}
                icon={documnentIcons[icon as keyof typeof documnentIcons]}
              />
              {record?.is_confidential && <div className={cn(classes.confidentialIcon, 'confidential')}>
                <Tooltip title={t('Documents.table.confidential')}>
                  <Assets.ShieldIcon />
                </Tooltip>
              </div>}
            </div>
            <span className={classes.fileName}>{record.name}</span>
          </div>
        );
      },
    },
    {
      title: (
        <span className={classes.userColumnTitle}>
          {t('RecycleBin.table.user')}
          <Tooltip title={t('RecycleBin.table.userTooltip')}>
            <QuestionCircleOutlined className={classes.questionMark}/>
            {/* <span className={classes.questionMark}>?</span> */}
          </Tooltip>
        </span>
      ),
      dataIndex: 'deletion_author',
      key: 'deletion_author',
      filters: getUserFilters(data),
      onFilter: (value, record) => record.deletion_author?.id === value,
      width: '25%',
      ellipsis: true,
      render: (_: any, record: IRecycleBinItem) => record.deletion_author?.name || '-',
    },
    {
      title: t('RecycleBin.table.deletedAt'),
      dataIndex: 'deleted_at',
      key: 'deleted_at',
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.deleted_at).getTime() - new Date(b.deleted_at).getTime(),
      showSorterTooltip: { mouseEnterDelay: 1 },
      width: '20%',
      ellipsis: true,
      render: (value: string) => getDateWithTimeZone(value, userData?.date_format || 'international'),
    },
    {
      title: t('RecycleBin.table.daysLeft'),
      dataIndex: 'days_left',
      key: 'days_left',
      sorter: (a, b) => calculateDaysLeft(a.deleted_at) - calculateDaysLeft(b.deleted_at),
      showSorterTooltip: { mouseEnterDelay: 1 },
      width: '20%',
      align: 'center',
      ellipsis: true,
      render: (_: any, record: IRecycleBinItem) => {
        const daysLeft = calculateDaysLeft(record.deleted_at);
        return (
          <span className={cn({ [classes.daysLeftDanger]: daysLeft <= 7 })}>
            {daysLeft}
          </span>
        );
      },
    },
  ];
